import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, X, Save } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminProducts = () => {
  const { token } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    price: 149,
    currency: 'GBP',
    images: '',
    category: 'sculpture',
    movie_reference: '',
    stock: 10,
    featured: false,
  });

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const openModal = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setFormData({
        name: product.name,
        slug: product.slug,
        description: product.description,
        price: product.price,
        currency: product.currency,
        images: product.images.join(', '),
        category: product.category,
        movie_reference: product.movie_reference,
        stock: product.stock,
        featured: product.featured,
      });
    } else {
      setEditingProduct(null);
      setFormData({
        name: '',
        slug: '',
        description: '',
        price: 149,
        currency: 'GBP',
        images: '',
        category: 'sculpture',
        movie_reference: '',
        stock: 10,
        featured: false,
      });
    }
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...formData,
      images: formData.images.split(',').map(img => img.trim()).filter(Boolean),
      price: parseFloat(formData.price),
      stock: parseInt(formData.stock),
    };

    try {
      if (editingProduct) {
        await axios.put(`${API}/admin/products/${editingProduct.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API}/admin/products`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      setIsModalOpen(false);
      fetchProducts();
    } catch (error) {
      console.error('Error saving product:', error);
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    
    try {
      await axios.delete(`${API}/admin/products/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
    }
  };

  return (
    <div data-testid="admin-products" className="p-6 lg:p-10">
      <div className="flex items-center justify-between mb-10">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            Products
          </h1>
          <p className="mt-2 text-neutral-500">Manage your product catalog</p>
        </div>
        <button
          onClick={() => openModal()}
          data-testid="add-product-button"
          className="btn-primary flex items-center gap-2"
        >
          <Plus size={18} />
          Add Product
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="spinner" />
        </div>
      ) : (
        <div className="overflow-x-auto bg-white border border-neutral-200">
          <table className="w-full">
            <thead>
              <tr className="border-b border-neutral-200 bg-neutral-50">
                <th className="text-left py-4 px-4 text-neutral-500 text-sm uppercase tracking-wider font-medium">Image</th>
                <th className="text-left py-4 px-4 text-neutral-500 text-sm uppercase tracking-wider font-medium">Name</th>
                <th className="text-left py-4 px-4 text-neutral-500 text-sm uppercase tracking-wider font-medium">Price</th>
                <th className="text-left py-4 px-4 text-neutral-500 text-sm uppercase tracking-wider font-medium">Stock</th>
                <th className="text-left py-4 px-4 text-neutral-500 text-sm uppercase tracking-wider font-medium">Featured</th>
                <th className="text-right py-4 px-4 text-neutral-500 text-sm uppercase tracking-wider font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map((product) => (
                <tr key={product.id} className="border-b border-neutral-100 hover:bg-neutral-50">
                  <td className="py-4 px-4">
                    <div className="w-12 h-12 bg-neutral-100">
                      {product.images?.[0] && (
                        <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover" />
                      )}
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <p className="font-medium">{product.name}</p>
                    <p className="text-neutral-500 text-sm">{product.movie_reference}</p>
                  </td>
                  <td className="py-4 px-4 font-medium">£{product.price.toFixed(2)}</td>
                  <td className="py-4 px-4">{product.stock}</td>
                  <td className="py-4 px-4">
                    {product.featured ? (
                      <span className="text-green-600 text-sm">Yes</span>
                    ) : (
                      <span className="text-neutral-400 text-sm">No</span>
                    )}
                  </td>
                  <td className="py-4 px-4 text-right">
                    <button
                      onClick={() => openModal(product)}
                      data-testid={`edit-product-${product.id}`}
                      className="p-2 text-neutral-400 hover:text-black transition-colors"
                    >
                      <Edit size={18} />
                    </button>
                    <button
                      onClick={() => handleDelete(product.id)}
                      data-testid={`delete-product-${product.id}`}
                      className="p-2 text-neutral-400 hover:text-red-600 transition-colors"
                    >
                      <Trash2 size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsModalOpen(false)}
              className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="fixed inset-4 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-full md:max-w-2xl bg-white border border-neutral-200 z-50 overflow-y-auto max-h-[90vh]"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold tracking-tight">
                    {editingProduct ? 'Edit Product' : 'Add Product'}
                  </h2>
                  <button onClick={() => setIsModalOpen(false)} className="p-2 hover:text-neutral-600">
                    <X size={24} />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Product name *"
                      required
                      data-testid="product-name-input"
                      className="input-underline"
                    />
                    <input
                      type="text"
                      name="slug"
                      value={formData.slug}
                      onChange={handleChange}
                      placeholder="URL slug *"
                      required
                      data-testid="product-slug-input"
                      className="input-underline"
                    />
                  </div>

                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Description *"
                    required
                    rows={3}
                    data-testid="product-description-input"
                    className="input-underline resize-none"
                  />

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <input
                      type="number"
                      name="price"
                      value={formData.price}
                      onChange={handleChange}
                      placeholder="Price *"
                      required
                      step="0.01"
                      data-testid="product-price-input"
                      className="input-underline"
                    />
                    <input
                      type="text"
                      name="currency"
                      value={formData.currency}
                      onChange={handleChange}
                      placeholder="Currency"
                      data-testid="product-currency-input"
                      className="input-underline"
                    />
                    <input
                      type="number"
                      name="stock"
                      value={formData.stock}
                      onChange={handleChange}
                      placeholder="Stock"
                      data-testid="product-stock-input"
                      className="input-underline"
                    />
                  </div>

                  <input
                    type="text"
                    name="images"
                    value={formData.images}
                    onChange={handleChange}
                    placeholder="Image URLs (comma separated)"
                    data-testid="product-images-input"
                    className="input-underline"
                  />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input
                      type="text"
                      name="category"
                      value={formData.category}
                      onChange={handleChange}
                      placeholder="Category"
                      data-testid="product-category-input"
                      className="input-underline"
                    />
                    <input
                      type="text"
                      name="movie_reference"
                      value={formData.movie_reference}
                      onChange={handleChange}
                      placeholder="Movie/Series reference"
                      data-testid="product-movie-input"
                      className="input-underline"
                    />
                  </div>

                  <label className="flex items-center gap-3 py-4">
                    <input
                      type="checkbox"
                      name="featured"
                      checked={formData.featured}
                      onChange={handleChange}
                      data-testid="product-featured-input"
                      className="w-5 h-5"
                    />
                    <span>Featured product</span>
                  </label>

                  <button
                    type="submit"
                    data-testid="save-product-button"
                    className="w-full btn-primary flex items-center justify-center gap-2"
                  >
                    <Save size={18} />
                    {editingProduct ? 'Update Product' : 'Create Product'}
                  </button>
                </form>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminProducts;
